import Vue from 'vue';
import VueRouter from 'vue-router';
import Guard from '@/services/middleware';
import LogoutMiddleware from '@/services/middleware/LogoutMiddleware';
import { hasPermission } from '@/services/auth/user';

Vue.use(VueRouter);

const routes = [
  // Onepage (auto-redirect baseado em token)
  {
    path: '/',
    name: 'onepage',
    beforeEnter: Guard.unauthentic,
  },

  // Auth (públicas)
  {
    path: '/login',
    name: 'login',
    meta: { title: 'Autenticação' },
    component: () => import('@/views/autenticacao/LoginView.vue'),
  },
  {
    path: '/logout',
    name: 'logout',
    beforeEnter: LogoutMiddleware.logout,
  },

  // Área autenticada — AppLayout com Sidebar
  {
    path: '/',
    component: () => import('@/components/layout/AppLayout.vue'),
    beforeEnter: Guard.auth,
    children: [
      // Dashboard
      {
        path: 'dashboard',
        name: 'dashboard',
        meta: { title: 'Dashboard' },
        component: () => import('@/views/dashboard/DashboardView.vue'),
      },

      // GESTÃO
      {
        path: 'empresas',
        name: 'companies',
        meta: { title: 'Empresas' },
        component: () => import('@/views/companies/CompaniesView.vue'),
      },
      {
        path: 'setores',
        name: 'sectors',
        meta: { title: 'Setores' },
        component: () => import('@/views/sectors/SectorsView.vue'),
      },
      {
        path: 'colaboradores',
        name: 'employees',
        meta: { title: 'Colaboradores' },
        component: () => import('@/views/employees/EmployeesView.vue'),
      },
      {
        path: 'riscos',
        name: 'risks',
        meta: { title: 'Riscos Psicossociais' },
        component: () => import('@/views/risks/RisksView.vue'),
      },
      {
        path: 'planos-acao',
        name: 'actionPlans',
        meta: { title: 'Planos de Ação' },
        component: () => import('@/views/action-plans/ActionPlansView.vue'),
      },
      {
        path: 'capacitacoes',
        name: 'trainings',
        meta: { title: 'Capacitações' },
        component: () => import('@/views/trainings/TrainingsView.vue'),
      },
      {
        path: 'evidencias',
        name: 'evidences',
        meta: { title: 'Evidências' },
        component: () => import('@/views/evidences/EvidencesView.vue'),
      },
      {
        path: 'atendimento',
        name: 'psychologicalCares',
        meta: { title: 'Atendimento Psicológico' },
        component: () => import('@/views/psychological-cares/PsychologicalCaresView.vue'),
      },

      // ADMINISTRAÇÃO
      {
        path: 'usuarios',
        name: 'users',
        meta: { title: 'Usuários', permissions: ['users.view'] },
        component: () => import('@/views/users/UsersView.vue'),
      },

      // DIAGNÓSTICO (placeholders — Fase 3/4)
      // Empresa só visualiza o histórico — não inicia diagnóstico nem gera IA.
      {
        path: 'diagnostico-inicial',
        name: 'initialDiagnostic',
        meta: { title: 'Diagnóstico Inicial', permissions: ['assessments.create'] },
        component: () => import('@/views/diagnostics/InitialDiagnosticView.vue'),
      },
      {
        path: 'historico-diagnosticos',
        name: 'diagnosticHistory',
        meta: { title: 'Histórico de Diagnósticos' },
        component: () => import('@/views/diagnostics/DiagnosticHistoryView.vue'),
      },
      {
        path: 'diagnostico',
        name: 'diagnostics',
        meta: { title: 'IA Diagnóstica', permissions: ['assessments.generate_ai'] },
        component: () => import('@/views/diagnostics/DiagnosticsView.vue'),
      },

      // CANAL DE DENÚNCIAS
      {
        path: 'denuncias',
        name: 'reports',
        meta: { title: 'Denúncias', permissions: ['denuncias.view'] },
        component: () => import('@/views/denuncias/ReportsView.vue'),
      },
      {
        path: 'canais-denuncias',
        name: 'reportChannels',
        meta: { title: 'Canais de Denúncia', permissions: ['denuncias.channels'] },
        component: () => import('@/views/denuncias/ChannelsView.vue'),
      },
      {
        // Formulário de UM canal (unidade) — acessado a partir do hub de Canais.
        path: 'canais-denuncias/:unitId/formulario',
        name: 'reportForm',
        meta: { title: 'Formulário do Canal', permissions: ['denuncias.forms'] },
        component: () => import('@/views/denuncias/ReportFormView.vue'),
      },

      // Redirect /home → dashboard (LoginView atual aponta pra /home)
      {
        path: 'home',
        redirect: { name: 'dashboard' },
      },
    ],
  },

  // Canal de Denúncias — página PÚBLICA por slug de unidade (sem auth).
  // Ex.: app.luzrh.com.br/clientetal — deve ficar por ÚLTIMO (catch-all) para
  // não capturar rotas conhecidas (/login, /dashboard, etc.).
  {
    path: '/:slug',
    name: 'publicChannel',
    meta: { title: 'Canal de Denúncias', public: true },
    component: () => import('@/views/public/PublicChannelView.vue'),
  },
];

const router = new VueRouter({
  mode: 'history',
  routes,
  linkActiveClass: 'active-item-sidebar',
});

router.beforeEach((to, from, next) => {
  const appName = process.env.VUE_APP_TITLE || 'Luz RH';
  document.title = ['', undefined, false, null].includes(to?.meta?.title)
    ? appName
    : `${appName} - ${to.meta.title}`;

  // Guard por permissions: a rota declara `meta.permissions: ['slug1', 'slug2']`.
  // User precisa ter AO MENOS UMA das permissions pra entrar (OR-OR).
  // Sem nenhuma → redireciona pro dashboard com toast.
  const required = Array.isArray(to?.meta?.permissions) ? to.meta.permissions : [];
  if (required.length > 0 && !required.some((slug) => hasPermission(slug))) {
    if (Vue.toasted) {
      Vue.toasted.error('<b>Acesso negado</b>&nbsp;&nbsp;Você não tem permissão para acessar essa página.');
    }
    return next({ name: 'dashboard' });
  }

  next();
});

export default router;
